import { preloadTimePanelData } from '../actions/publicActions';
import { getDurationBucket, getViewDuration } from '../selectors/telemetrySelectors';
import { getSelectedCalendarItemId } from '../selectors/timePanelStoreSelectors';
import { logUsage } from 'owa-analytics-actions';
import { lazyResetCalendarView } from 'owa-calendar-list';
import { closeNotesEditor } from 'owa-calendar-notes-editor';
import { lazyUpdateSelectedEventId } from 'owa-event-details';
import { popView } from 'owa-time-panel-bootstrap';
import { orchestrator } from 'satcheljs';
import {
    getTimePanelCustomData,
    TimePanelChangeViewCustomData,
    timePanelOptions,
} from '../datapoints';
import {
    panelClosed,
    panelOpened,
    pushNewView,
    updateSelectedCalendarEvent,
    updateCurrentView,
    resetPanelViewOpenTimeStamp,
} from '../actions/timePanelStoreActions';
import {
    popTimePanelView,
    pushTimePanelView,
    updateTopTimePanelView,
    PanelView,
    getPreviousPanelView,
    getPanelViewType,
    getStackLength,
    getCurrentPanelView,
} from 'owa-time-panel-view-stack';

// Core orchestrators

export const preloadTimePanelDataOrchestrator = orchestrator(preloadTimePanelData, async () => {
    await loadPanelData();
});

export const panelOpenedOrchestrator = orchestrator(panelOpened, () => {
    // log change view event since it will otherwise not be captured by standard lifecycle
    logChangeView(getCurrentPanelView(), true);
});

export const panelClosedOrchestrator = orchestrator(panelClosed, () => {
    // log change view event since it will otherwise not be captured by standard lifecycle
    logChangeView(null);
    // clean up the view stack
    while (getStackLength() > 1) {
        popTimePanelView();
    }
    // clean up notes editor in case an event details view is buried somewhere in the stack
    const id = getSelectedCalendarItemId();
    if (id) {
        closeNotesEditor(id);
    }
    // reset calendar view for next session
    lazyResetCalendarView.importAndExecute();
});

// Orchestrators for view stack

export const pushNewViewOrchestrator = orchestrator(pushNewView, actionMessage => {
    const { newView } = actionMessage;
    logChangeView(newView);
    pushTimePanelView(newView);
});

export const updateCurrentViewOrchestrator = orchestrator(updateCurrentView, actionMessage => {
    const { updatedView } = actionMessage;
    logChangeView(updatedView);
    updateTopTimePanelView(updatedView);
});

export const popViewOrchestrator = orchestrator(popView, () => {
    logChangeView(getPreviousPanelView());
    popTimePanelView();
});

orchestrator(popTimePanelView, updateTimeStamp);
orchestrator(updateTopTimePanelView, updateTimeStamp);
orchestrator(pushTimePanelView, updateTimeStamp);

function updateTimeStamp() {
    resetPanelViewOpenTimeStamp();
}

// Orchestrators for shared event callbacks

export const updateSelectedCalendarEventOrchestrator = orchestrator(
    updateSelectedCalendarEvent,
    actionMessage => {
        const { selectedCalendarEventId } = actionMessage;
        lazyUpdateSelectedEventId.importAndExecute(selectedCalendarEventId);
    }
);

// Helper functions

function logChangeView(nextView: PanelView, isInitialView?: boolean): void {
    // current view will become the previous view (unless this is the initial view on panel load)
    const previousView = isInitialView ? null : getCurrentPanelView();
    const previousViewDuration = isInitialView ? 0 : getViewDuration();
    const previousViewDurationBucket = getDurationBucket(previousViewDuration);
    logUsage(
        'timePanelActionChangeView',
        getTimePanelCustomData<TimePanelChangeViewCustomData>({
            // standard logging
            previousView,
            previousViewType: getPanelViewType(previousView),
            previousViewDuration,
            nextView,
            nextViewType: getPanelViewType(nextView),
            // long-term logging
            owa_1: previousView,
            owa_2: getPanelViewType(previousView),
            owa_3: previousViewDurationBucket.lowerBoundMinutes,
            owa_4: previousViewDurationBucket.upperBoundMinutes,
            owa_5: nextView,
            owa_6: getPanelViewType(nextView),
        }),
        timePanelOptions
    );
}

async function loadPanelData(): Promise<void> {
    // VSO 46502: any prefetch we can do to pre-load agenda view data should be done here, including calling pre-fetch functions from mogenda
}
